import * as yup from 'yup'

// order header details schema
const headerSchema = yup.object({
  ID: yup.string().max(50).nullable(),
  EntryDate: yup.string().nullable(),
  ReferenceNumber: yup.string().nullable(),
  PONumber: yup.string().nullable(),
  Comments: yup.string().nullable(),
  IpAddress: yup.string().nullable(),
  InsertDate: yup.string().nullable(),
  UTCEntryDateTime: yup.string().nullable(),
})

// person variable item schema
const personVariableItemSchema = yup.object({
  PersonVariable: yup.object({
    VariableField: yup.object({
      FieldName: yup.string().nullable(),
    }),
    Value: yup.boolean().nullable(),
    ValueDescription: yup.string().nullable(),
  }),
})

// ordered by schema
const veracoreRecipientSchema = yup.object({
  Prefix: yup.string().nullable(),
  FirstName: yup.string().max(15).nullable(),
  MiddleInitial: yup.string().nullable(),
  LastName: yup.string().max(100).nullable(),
  Suffix: yup.string().nullable(),
  CompanyName: yup.string().nullable(),
  Title: yup.string().nullable(),
  Address1: yup.string().max(255).nullable(),
  Address2: yup.string().max(255).nullable(),
  Address3: yup.string().nullable(),
  City: yup.string().max(50).nullable(),
  State: yup.string().max(20).nullable(),
  PostalCode: yup.string().max(15).nullable(),
  Country: yup.string().max(50).nullable(),
  Phone: yup.string().max(20).nullable(),
  Fax: yup.string().nullable(),
  Email: yup.string().max(255).required().email(),
  UID: yup.string().nullable(),
  TaxExempt: yup.boolean().nullable(),
  TaxExemptID: yup.string().nullable(),
  TaxExemptIDApproved: yup.boolean().nullable(),
  Commercial: yup.boolean().nullable(),
  Variables: yup.array().of(personVariableItemSchema).nullable(),
  FullName: yup.string().nullable(),
  FullNameWithSuffix: yup.string().nullable(),
  CityStateZip: yup.string().nullable(),
  CityStateZipCountry: yup.string().nullable(),
})

// bill to schema
const orderBillToSchema = yup.object({
  Prefix: yup.string().nullable(),
  FirstName: yup.string().max(15).nullable(),
  MiddleInitial: yup.string().nullable(),
  LastName: yup.string().max(100).nullable(),
  Suffix: yup.string().nullable(),
  CompanyName: yup.string().nullable(),
  Title: yup.string().nullable(),
  Address1: yup.string().max(255).nullable(),
  Address2: yup.string().max(255).nullable(),
  Address3: yup.string().nullable(),
  City: yup.string().max(50).nullable(),
  State: yup.string().max(20).nullable(),
  PostalCode: yup.string().max(15).nullable(),
  Country: yup.string().max(50).nullable(),
  Phone: yup.string().max(20).nullable(),
  Fax: yup.string().nullable(),
  Email: yup.string().max(255).nullable(),
  UID: yup.string().nullable(),
  TaxExempt: yup.boolean().nullable(),
  TaxExemptID: yup.string().nullable(),
  TaxExemptApproved: yup.boolean().nullable(),
  Commercial: yup.boolean().nullable(),
  Variables: yup.array().of(personVariableItemSchema).nullable(),
  Flag: yup.string().nullable(),
  FullName: yup.string().nullable(),
  FullNameWithSuffix: yup.string().nullable(),
  CityStateZip: yup.string().nullable(),
  CityStateZipCountry: yup.string().nullable(),
  CompoundAddress: yup.string().nullable(),
})

// ship to schema
const orderShipToSchema = yup.object({
  Flag: yup.string().nullable(),
  Key: yup.string().nullable(),
  NeededBy: yup.string().nullable(),
  ReleaseDate: yup.string().nullable(),
  Rush: yup.boolean().nullable(),
  RushHandling: yup.number().nullable(),
  Comments: yup.string().nullable(),
  ThirdPartyType: yup.number().nullable(),
  ThirdPartyAccountNumber: yup.string().nullable(),
  FreightCode: yup.string().nullable(),
  FreightCodeDescription: yup.string().nullable(),
  SpecialHandlingCharge: yup.number().nullable(),
  FullName: yup.string().nullable(),
  FullNameWithSuffix: yup.string().nullable(),
  CityStateZip: yup.string().nullable(),
  CityStateZipCountry: yup.string().nullable(),
  CompoundAddress: yup.string().nullable(),
  ShippingHandlingTaxAmount: yup.number().nullable(),
  ShippingHandlingTaxPercent: yup.number().nullable(),
  Variables: yup.array().of(personVariableItemSchema).nullable(),
})

// Each offer item schema
const offerItemSchema = yup.object({
  Offer: yup.object({
    ID: yup.string().required(),
  }),
  Quantity: yup.number().required(),
  UnitPrice: yup.number().required(),
})

// Classification schema
const classificationSchema = yup.object({
  CampaignID: yup.string().nullable(),
  CustomerCode: yup.string().nullable(),
  Store: yup.string().nullable(),
  Department: yup.string().nullable(),
  DistributionCenter: yup.string().nullable(),
  Vendor: yup.string().nullable(),
})

// Money schema
const moneySchema = yup.object({
  DiscountAmount: yup.number().nullable(),
  DiscountPercent: yup.number().nullable(),
  DiscountCode: yup.string().nullable(),
  Coupon: yup.string().nullable(),
  SpecialHandlingCharge: yup.number().nullable(),
  CreditAmount: yup.number().nullable(),
  TaxPercent: yup.number().nullable(),
  NoChargeAmount: yup.number().nullable(),
  TaxAmount: yup.number().nullable(),
})

// Order variable item schema
const orderVariableItemSchema = yup.object({
  OrderVariable: yup.object({
    VariableField: yup.object({
      FieldName: yup.string().nullable(),
    }),
    Value: yup.boolean().nullable(),
    ValueDescription: yup.string().nullable(),
  }),
})

// Final Create Order schema
export const veracoreCreateOrderSchema = yup.object({
  Header: headerSchema,
  Classification: classificationSchema.nullable(),
  Money: moneySchema.nullable(),
  OrderVariables: yup.array().of(orderVariableItemSchema).nullable(),
  OrderedBy: veracoreRecipientSchema.required(),
  ShipTo: orderShipToSchema.required(),
  BillTo: orderBillToSchema.required(),
  Offers: yup.array().of(offerItemSchema).required().min(1, 'At least one offer item is required'),
})

export type VeracoreCreateOrder = yup.InferType<typeof veracoreCreateOrderSchema>
